import React, { useEffect, useRef, useState } from "react";
import { FileText, Camera, Send, Trash2, Sparkles, ChevronLeft, Image as ImageIcon, Save, Undo2, Redo2, ChevronDown, ChevronRight, CalendarDays } from "lucide-react";
import { apiFetch } from "../lib/api.js";
import { getCurrentUser } from "../lib/auth.js";
import ConfirmDialog from "./ConfirmDialog.jsx";
import { Modal } from "./ui.jsx";

// Nutriční reporty (13.7.2026) — detail klienta → Výživa.
// Report z konzultace + analýza jídelníčku: editor s obrázky, AI koncept
// z přepisu konzultace, odeslání klientce e-mailem jedním klikem, historie.
// Data: /api/nutri-reporty.

// Šablona 1:1 podle nejnovějších reportů (2025, „Nutriční report z online
// konzultace" — Kristina Žatečková): úvod s handoutem, ✅/❌ hodnocení,
// číslovaná doporučení vč. pravidla 3/3 a nastavení příjmu, svačiny, pohyb,
// Obecně + Závěr s odkazem na recenze a upsell.
const SABLONA_KONZULTACE = (jmeno) => `<p>Dobrý den, <b>${jmeno}</b>,</p>
<p>zasílám vám shrnutí naší nutriční konzultace. Pro první měsíce bude zásadní <b>držet se doporučení, která najdete níže.</b></p>
<p>V příloze také přikládám handout, který obsahuje popis jednotlivých živin a jejich zdroje.</p>
<p>Tady už je <b>hodnocení vašeho jídelníčku</b>, kde zjistíte, v čem děláte chyby a co naopak děláte dobře. Pod ním najdete i <b>konkrétní doporučení ke změně</b>, která budou <b>hlavní pro následující měsíce.</b></p>
<h3>Co bylo dobře:</h3>
<p>✅ …<br>✅ …<br>✅ …</p>
<h3>Co bylo špatně:</h3>
<p>❌ …<br>❌ …<br>🔜 …</p>
<h3>Co je potřeba změnit:</h3>
<h3>1. Jezte dostatek bílkovin a vlákniny v každém jídle</h3>
<p>Každé vaše jídlo by mělo obsahovat dostatek <b>bílkovin</b>, především pak hlavní jídla. Bílkoviny jsou <b>nejvíce sytící makroživinou</b>, potřebujete jich mít dostatek, abyste neměla hlad.</p>
<p><b>Ve zkratce:</b> řiďte se pravidlem 3/3. 1/3 talíře by měly tvořit <b>bílkoviny</b>, 1/3 <b>sacharidy</b> a 1/3 <b>zelenina</b>.</p>
<h3>2. Jezte pravidelně a dostatečně</h3>
<p>Nastavte si příjem energie na <b>… kcal/den</b> a živiny následovně:</p>
<p><b>Sacharidy:</b> … g/den<br><b>Bílkoviny:</b> … g/den<br><b>Tuky:</b> … g/den</p>
<p>Každý den si dejte <b>snídani, svačinu, oběd, svačinu a večeři</b>, všechna jídla dostatečně velká a s dostatkem živin.</p>
<h3>3. Vylepšete vaše svačiny</h3>
<p>I svačiny by měly obsahovat <b>dostatek energie a živin</b>. Vhodná je například kombinace kousku <b>ovoce či zeleniny</b>, <b>neslazeného mléčného výrobku</b> (řecký jogurt, skyr, tvaroh) a <b>porce ořechů</b>.</p>
<h3>4. Přidejte co nejvíce (přirozeného) pohybu</h3>
<p>Každý den ujděte <b>minimálně … kroků</b>. Přirozený pohyb dělá největší parádu, díky němu spálíte přes den nejvíce energie.</p>
<h3>Obecně:</h3>
<p>U všech hlavních jídel myslete na to, že musí obsahovat dostatek všech živin, tedy <b>bílkovin, komplexních sacharidů a vhodných tuků</b>. Na vašem talíři by se vždy měla vyskytovat porce <b>ovoce či zeleniny</b>.</p>
<h3>Závěr:</h3>
<p>Toto budou vaše úkoly, které vám pomohou se naučit lépe stravovat. Je důležité, abyste všechny zadané úkoly plnila pomalu, ale na 100 %.</p>
<p>Budeme moc vděční, pokud můžete věnovat minutu vašeho času pro napsání reference našich služeb na Google. <a href="https://g.page/r/Cb-8FBqzApCZEB0/review">Zde je odkaz.</a></p>
<p>Doporučuji vám pročíst si <a href="https://ladislavzatecka.cz/blog/">články u nás na blogu</a>, kde najdete spoustu zajímavých témat.</p>
<p>Pokud byste měla zájem o <b>jídelníček na míru</b> nebo <b>výživový koučink</b>, kde budeme váš jídelníček a pokroky dále monitorovat, stačí napsat.</p>`;

const SABLONA_ANALYZA = (jmeno) => `<p>Dobrý den, <b>${jmeno}</b>,</p>
<p>posílám vám analýzu vašich jídel z posledních dnů.</p>
<h3>PONDĚLÍ</h3>
<p><b>Snídaně: …</b><br>✅ …<br>❌ …<br>🔜 …</p>
<h3>Závěrečná doporučení</h3>
<p>1. …<br>2. …</p>`;

// Automatické ukládání rozepsaného reportu (27.8.2026, přání Ládi).
// Stejný princip jako u rozpracovaného jídelníčku: text se při psaní průběžně
// odkládá do prohlížeče. Koncept NENÍ uložená verze, klientka ho nevidí, ale
// když se okno zavře dřív, než se stihne kliknout na „Uložit koncept",
// nabídne se při dalším otevření zpět.
const KONCEPT_KEY = (klientId, klic) => `fw40-nutri-report-koncept-${klientId}-${klic}`;

function fmtDate(d) {
  try { return new Date(d).toLocaleDateString("cs-CZ"); } catch { return ""; }
}

function fmtDateTime(d) {
  try {
    const x = new Date(d);
    return `${x.toLocaleDateString("cs-CZ")} ${String(x.getHours()).padStart(2, "0")}:${String(x.getMinutes()).padStart(2, "0")}`;
  } catch { return ""; }
}

// Hodnota pro <input type="datetime-local"> — za `dny` dní v `hodina`:00 lokálního času.
function presetKdy(dny, hodina) {
  const d = new Date();
  d.setDate(d.getDate() + dny);
  d.setHours(hodina, 0, 0, 0);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export default function NutriReportySection({ klientId, klientNotion, klientName, readOnly = false, onCount }) {
  const id = klientId || klientNotion;
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null); // id reportu v editoru, "new-konzultace"/"new-analyza" = nový
  const [nazev, setNazev] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");
  const [komu, setKomu] = useState("");
  const [predmet, setPredmet] = useState("");
  const [datumKonz, setDatumKonz] = useState(""); // ISO datum konzultace (z Reservio termínu nebo ručně)
  const [aiBusy, setAiBusy] = useState(false);
  const [prepisText, setPrepisText] = useState("");
  const [prepisOpen, setPrepisOpen] = useState(false);
  const [fotky, setFotky] = useState([]); // data URL fotek jídel (analýza)
  const [fotkyPopis, setFotkyPopis] = useState("");
  const [confirmState, setConfirmState] = useState(null);
  const [planOpen, setPlanOpen] = useState(false);      // panel "Poslat později"
  const [planKdy, setPlanKdy] = useState("");            // datetime-local hodnota
  const [histOpen, setHistOpen] = useState(false);      // Historie nutričních konzultací (6.8.2026)
  const [konceptObnoven, setKonceptObnoven] = useState(null); // { ulozeno, puvodni }
  const editorRef = useRef(null);
  const konceptTimer = useRef(null);
  const fileRef = useRef(null);
  const prepisFileRef = useRef(null);
  const fotkyRef = useRef(null);

  const user = getCurrentUser();
  const role = user?.role || "";
  const canManage = !readOnly && ["owner", "manager", "csm", "nutritionist"].includes(role);
  const isOwner = !readOnly && role === "owner";

  const load = async () => {
    if (!id) return;
    try {
      const r = await apiFetch(`/api/nutri-reporty?klient_id=${encodeURIComponent(id)}`);
      const j = await r.json();
      if (j.ok) { setData(j); if (!komu) setKomu(j.klientEmail || ""); }
    } catch (_) { /* ticho */ }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const post = async (body) => {
    setErr("");
    const r = await apiFetch("/api/nutri-reporty", { method: "POST", body: JSON.stringify({ ...body, klient_id: id }) });
    const j = await r.json();
    if (!j.ok) { setErr(j.error || "Akce se nepovedla"); return null; }
    return j;
  };

  const reporty = data?.reporty || [];
  // 9.9.2026 — nadrazena podsekce si ukazuje pocet reportu v hlavicce.
  useEffect(() => { if (typeof onCount === "function") onCount(reporty.length); }, [reporty.length, onCount]);
  const current = reporty.find((x) => x.id === openId) || null;
  const isNew = String(openId || "").startsWith("new-");
  const typ = isNew ? openId.replace("new-", "") : current?.typ || "konzultace";
  const jeKoncept = isNew || current?.stav === "koncept";
  const krestni = String(klientName || "").split(/\s+/)[0] || "";

  // Termíny nutri konzultací z Reservio (PG bookings) — nabídka pro datum reportu.
  const terminy = data?.terminy || [];
  const volneTerminy = terminy.filter((t) => !t.maReport);
  // Historie konzultací = všechny nutri rezervace z Reservia (3 roky zpět).
  const historie = data?.historie || [];

  // ── Automatický koncept v prohlížeči ─────────────────────────────
  const nactiKoncept = (klic) => {
    try {
      const raw = localStorage.getItem(KONCEPT_KEY(id, klic));
      if (!raw) return null;
      const o = JSON.parse(raw);
      return o && typeof o.obsah === "string" ? o : null;
    } catch { return null; } // poškozený nebo nedostupný koncept ignorujeme
  };
  const smazKoncept = (klic) => {
    try { localStorage.removeItem(KONCEPT_KEY(id, klic == null ? openId : klic)); } catch { /* anonymní okno */ }
  };
  const zapisKoncept = () => {
    if (!openId || !editorRef.current) return;
    try {
      localStorage.setItem(KONCEPT_KEY(id, openId), JSON.stringify({
        ulozeno: new Date().toISOString(),
        nazev, predmet, datumKonz,
        obsah: editorRef.current.innerHTML,
      }));
    } catch { /* plná kvóta / anonymní okno: koncept prostě nebude */ }
  };
  // Psaní v editoru = jeden zápis po 600 ms klidu, ne po každém znaku.
  const naplanujZapis = () => {
    if (konceptTimer.current) clearTimeout(konceptTimer.current);
    konceptTimer.current = setTimeout(zapisKoncept, 600);
  };
  useEffect(() => () => { if (konceptTimer.current) clearTimeout(konceptTimer.current); }, []);
  // Změna názvu, předmětu nebo data se do konceptu propíše taky.
  useEffect(() => { if (openId) naplanujZapis(); }, [nazev, predmet, datumKonz]); // eslint-disable-line react-hooks/exhaustive-deps

  // Naplní editor: nejdřív uložená verze, a když leží novější neuložený
  // koncept, dosadí se on a nad editorem se rozsvítí pruh s nabídkou zahodit.
  const naplnEditor = (klic, zaklad) => {
    const ulozeny = nactiKoncept(klic);
    setTimeout(() => {
      if (!editorRef.current) return;
      if (ulozeny && String(ulozeny.obsah || "") !== String(zaklad || "")) {
        editorRef.current.innerHTML = ulozeny.obsah;
        if (ulozeny.nazev) setNazev(ulozeny.nazev);
        if (ulozeny.predmet) setPredmet(ulozeny.predmet);
        if (ulozeny.datumKonz) setDatumKonz(ulozeny.datumKonz);
        setKonceptObnoven({ ulozeno: ulozeny.ulozeno, puvodni: zaklad || "" });
      } else {
        editorRef.current.innerHTML = zaklad || "";
        setKonceptObnoven(null);
      }
    }, 0);
  };

  // „Zahodit" u nového reportu musí smazat i koncept v prohlížeči. Jinak se
  // editor sice zavře, ale text se hned nabídne zpátky a vypadá to jako chyba.
  const zahoditRozepsany = () => {
    setConfirmState({
      title: "Zahodit rozepsaný report?",
      description: <>Napsaný text se smaže i z konceptu uloženého v prohlížeči. Tohle nejde vzít zpět.</>,
      confirmLabel: "Zahodit",
      onConfirm: () => {
        setConfirmState(null);
        if (konceptTimer.current) clearTimeout(konceptTimer.current);
        smazKoncept(openId);
        setKonceptObnoven(null);
        setOpenId(null);
      },
    });
  };

  const zahoditKoncept = () => {
    setConfirmState({
      title: "Zahodit obnovený text?",
      description: <>Editor se vrátí k poslední <strong>uložené</strong> verzi reportu a obnovený text se smaže. Tohle nejde vzít zpět.</>,
      confirmLabel: "Zahodit",
      onConfirm: () => {
        setConfirmState(null);
        smazKoncept();
        if (editorRef.current) editorRef.current.innerHTML = konceptObnoven?.puvodni || "";
        setKonceptObnoven(null);
      },
    });
  };

  const openEditor = (rep) => {
    setOpenId(rep.id);
    setNazev(rep.nazev || "");
    setPredmet(rep.typ === "analyza" ? "Analýza vašeho jídelníčku (FitWay40)" : "Nutriční report z online konzultace");
    setDatumKonz(rep.datum_konzultace ? String(rep.datum_konzultace).slice(0, 10) : "");
    if (rep.stav === "koncept") naplnEditor(rep.id, rep.obsah || "");
    else setTimeout(() => { if (editorRef.current) editorRef.current.innerHTML = rep.obsah || ""; }, 0);
  };
  const openNew = (t) => {
    setOpenId(`new-${t}`);
    setNazev(t === "analyza" ? "Analýza jídelníčku" : "Report z nutriční konzultace");
    setPredmet(t === "analyza" ? "Analýza vašeho jídelníčku (FitWay40)" : "Nutriční report z online konzultace");
    // Předvyplnit nejnovější Reservio termín bez reportu (jen u konzultace).
    setDatumKonz(t === "konzultace" && volneTerminy[0] ? volneTerminy[0].datum : "");
    naplnEditor(`new-${t}`, t === "analyza" ? SABLONA_ANALYZA(krestni) : SABLONA_KONZULTACE(krestni));
  };

  const save = async () => {
    if (!editorRef.current) return null;
    const klicPred = openId;
    setSaving(true);
    const j = await post({ action: "save", id: isNew ? "" : openId, typ, nazev, obsah: editorRef.current.innerHTML, datum_konzultace: datumKonz || null });
    setSaving(false);
    if (j) {
      setSaved(true); setTimeout(() => setSaved(false), 2000);
      // Uloženou verzí koncept v prohlížeči ztrácí smysl.
      if (konceptTimer.current) clearTimeout(konceptTimer.current);
      smazKoncept(klicPred);
      if (j.id && j.id !== klicPred) smazKoncept(j.id);
      setKonceptObnoven(null);
      if (isNew) setOpenId(j.id);
      await load();
      return j.id;
    }
    return null;
  };

  // Jen uložit (bez odeslání) → zpět na seznam reportů.
  const ulozitAZavrit = async () => {
    const savedId = await save();
    if (savedId) { setOpenId(null); await load(); }
  };

  const smazat = (rep) => {
    setConfirmState({
      title: rep.stav === "koncept" ? "Smazat koncept?" : "Smazat odeslaný report?",
      description: <>Report „{rep.nazev}" se nenávratně smaže{rep.stav !== "koncept" ? " včetně záznamu o odeslání" : ""}.</>,
      confirmLabel: "Smazat",
      onConfirm: async () => {
        setConfirmState(null);
        if (await post({ action: "smazat", id: rep.id })) { smazKoncept(rep.id); if (openId === rep.id) setOpenId(null); await load(); }
      },
    });
  };

  const odeslat = () => {
    setConfirmState({
      title: "Odeslat report klientce?",
      description: <>Report se odešle na <strong>{komu}</strong> z adresy info@zateckal.cz a poté už nepůjde upravit.</>,
      confirmLabel: "Odeslat ✉️",
      onConfirm: async () => {
        setConfirmState(null);
        const savedId = await save();
        if (!savedId) return;
        setSaving(true);
        const j = await post({ action: "send", id: savedId, komu, predmet });
        setSaving(false);
        if (j) { setOpenId(null); await load(); }
      },
    });
  };

  // Plánované odeslání (14.7.2026): uloží koncept a naplánuje čas — odešle
  // to backend (cron každých 10 minut), report se do té doby zamkne.
  const naplanovat = async () => {
    if (!planKdy) { setErr("Vyberte datum a čas odeslání."); return; }
    const savedId = await save();
    if (!savedId) return;
    setSaving(true);
    const j = await post({ action: "naplanovat", id: savedId, kdy: new Date(planKdy).toISOString(), komu, predmet });
    setSaving(false);
    if (j) { setPlanOpen(false); setPlanKdy(""); setOpenId(null); await load(); }
  };

  const zrusitPlan = async (rep) => {
    const j = await post({ action: "naplanovat-zrusit", id: rep.id });
    if (j) await load();
  };

  // 14.7.2026 fix: po zrušení plánu (naplanovano → koncept) se editor mountne
  // znovu a byl PRÁZDNÝ — openEditor plnil innerHTML jen při otevření, kdy
  // místo editoru byl read-only náhled (ref tam není). Obsah v DB zůstal,
  // jen se nezobrazil; kdyby se pak uložilo, přepsal by se prázdnem.
  // Řešení: kdykoli se objeví prázdný editor otevřeného konceptu, naplnit ho.
  useEffect(() => {
    if (!jeKoncept || isNew || !current) return;
    const el = editorRef.current;
    if (el && el.innerHTML.trim() === "" && (current.obsah || "").trim() !== "") {
      el.innerHTML = current.obsah;
    }
  }, [jeKoncept, current]); // eslint-disable-line react-hooks/exhaustive-deps

  // Zmenšení fotky na max 900 px → JPEG data URL (sdílené: editor i analýza).
  const zmensiFotku = (file) => new Promise((resolve) => {
    if (!file || !file.type.startsWith("image/")) return resolve(null);
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, 900 / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = () => resolve(null);
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

  // Obrázek: zmenšit a vložit jako data URL do editoru.
  const vlozObrazek = async (file) => {
    const url = await zmensiFotku(file);
    if (url && editorRef.current) {
      editorRef.current.focus();
      document.execCommand("insertHTML", false, `<img src="${url}" style="max-width:100%;border-radius:10px;margin:8px 0" />`);
    }
  };

  const pridejFotky = async (files) => {
    const nove = [];
    for (const f of [...(files || [])].slice(0, 10)) {
      const url = await zmensiFotku(f);
      if (url) nove.push(url);
    }
    if (nove.length) setFotky((p) => [...p, ...nove].slice(0, 10));
  };

  const aiZPrepisu = async () => {
    if (prepisText.trim().length < 100) { setErr("Vložte nebo nahrajte celý přepis konzultace (aspoň pár odstavců)."); return; }
    setAiBusy(true);
    const j = await post({ action: "ai-koncept", typ, prepis: prepisText });
    setAiBusy(false);
    if (j && editorRef.current) { editorRef.current.innerHTML = j.obsah; setPrepisOpen(false); naplanujZapis(); }
  };

  const aiZFotek = async () => {
    if (!fotky.length) { setErr("Nahrajte aspoň jednu fotku jídla."); return; }
    setAiBusy(true);
    const j = await post({ action: "ai-koncept", typ: "analyza", fotky, popis: fotkyPopis });
    setAiBusy(false);
    if (j && editorRef.current) { editorRef.current.innerHTML = j.obsah; setPrepisOpen(false); naplanujZapis(); }
  };

  const nactiPrepisSoubor = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPrepisText(String(reader.result || ""));
    reader.readAsText(file, "utf-8");
  };

  const cmd = (c, v) => { if (editorRef.current) { editorRef.current.focus(); document.execCommand(c, false, v); } };
  const vlozText = (t) => cmd("insertText", t);

  // Sjednocení textu (15.7.2026, zpětná vazba nutritionist): text zkopírovaný
  // odjinud (Word, Google Docs, web) si nese cizí font, velikost a barvy.
  // ocistiHtml nechá strukturu (tučně, kurzíva, nadpisy, seznamy, odkazy,
  // obrázky) a všechno ostatní zahodí — text pak má písmo editoru.
  const ocistiHtml = (html) => {
    const box = document.createElement("div");
    box.innerHTML = String(html || "");
    const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const uzel = (node) => {
      if (node.nodeType === 3) return esc(node.textContent.replace(/\u00a0/g, " "));
      if (node.nodeType !== 1) return "";
      const tag = node.tagName;
      if (["STYLE", "SCRIPT", "HEAD", "TITLE", "META", "LINK"].includes(tag)) return "";
      if (tag === "BR") return "<br>";
      if (tag === "IMG") {
        const src = node.getAttribute("src") || "";
        return /^(data:image|https?:)/.test(src) ? `<img src="${src.replace(/"/g, "&quot;")}" style="max-width:100%;border-radius:10px;margin:8px 0" />` : "";
      }
      let deti = [...node.childNodes].map(uzel).join("");
      // Formát zadaný přes inline styl (Google Docs dělá <span style="font-weight:700">)
      const st = node.style || {};
      const fw = String(st.fontWeight || "");
      if (fw === "bold" || parseInt(fw, 10) >= 600) deti = `<b>${deti}</b>`;
      if ((st.fontStyle || "") === "italic") deti = `<i>${deti}</i>`;
      if (String(st.textDecoration || st.textDecorationLine || "").includes("underline")) deti = `<u>${deti}</u>`;
      if (tag === "B" || tag === "STRONG") return `<b>${deti}</b>`;
      if (tag === "I" || tag === "EM") return `<i>${deti}</i>`;
      if (tag === "U") return `<u>${deti}</u>`;
      if (/^H[1-6]$/.test(tag)) return deti.trim() ? `<h3>${deti}</h3>` : "";
      if (tag === "UL" || tag === "OL") { const t = tag.toLowerCase(); return `<${t}>${deti}</${t}>`; }
      if (tag === "LI") return `<li>${deti}</li>`;
      if (tag === "A") {
        const href = node.getAttribute("href") || "";
        return /^https?:/.test(href) ? `<a href="${href.replace(/"/g, "&quot;")}">${deti}</a>` : deti;
      }
      if (tag === "TD" || tag === "TH") return deti ? deti + " " : "";
      if (["P", "DIV", "SECTION", "ARTICLE", "BLOCKQUOTE", "FIGURE", "TR", "TABLE", "TBODY", "THEAD"].includes(tag)) {
        if (["TABLE", "TBODY", "THEAD"].includes(tag)) return deti;
        return deti.trim() ? `<div>${deti}</div>` : "";
      }
      return deti; // span, font a ostatní: jen obsah, formát se zahodí
    };
    return [...box.childNodes].map(uzel).join("");
  };

  // Tlačítko 🧹: sjednotí písmo výběru (nebo celého textu, když nic není
  // vybrané). Jde přes insertHTML, takže funguje i šipka zpět.
  const sjednotFormat = () => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed && el.contains(sel.anchorNode)) {
      const box = document.createElement("div");
      box.appendChild(sel.getRangeAt(0).cloneContents());
      document.execCommand("insertHTML", false, ocistiHtml(box.innerHTML));
    } else {
      const ciste = ocistiHtml(el.innerHTML);
      document.execCommand("selectAll", false, null);
      document.execCommand("insertHTML", false, ciste);
    }
  };

  // 🖌️ Štětec formátu (15.7.2026): 1. klik s kurzorem ve zformátovaném textu
  // formát zkopíruje, 2. klik s vybraným textem ho na výběr aplikuje.
  const [stetec, setStetec] = useState(null);

  // Vložení odkazu (14.9.2026, fáze 6): místo window.prompt malé okno v designu.
  // Editor při otevření okna ztratí výběr, proto se rozsah uloží a před
  // createLink zase obnoví.
  const [odkazOpen, setOdkazOpen] = useState(false);
  const [odkazUrl, setOdkazUrl] = useState("");
  const odkazRangeRef = useRef(null);
  const otevriOdkaz = () => {
    const sel = window.getSelection();
    odkazRangeRef.current = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
    setOdkazUrl("");
    setOdkazOpen(true);
  };
  const vlozOdkaz = () => {
    const u = odkazUrl.trim();
    setOdkazOpen(false);
    if (!u) return;
    const sel = window.getSelection();
    if (sel && odkazRangeRef.current) { sel.removeAllRanges(); sel.addRange(odkazRangeRef.current); }
    cmd("createLink", u);
  };

  const stetecKlik = () => {
    const el = editorRef.current;
    const sel = window.getSelection();
    if (!el || !sel || !sel.anchorNode || !el.contains(sel.anchorNode)) return;
    if (!stetec) {
      let n = sel.anchorNode;
      if (n.nodeType === 3) n = n.parentElement;
      if (!n) return;
      const cs = window.getComputedStyle(n);
      setStetec({
        bold: parseInt(cs.fontWeight, 10) >= 600,
        italic: cs.fontStyle === "italic",
        underline: String(cs.textDecorationLine || "").includes("underline"),
        color: cs.color,
        h3: !!(n.closest && n.closest("h3")),
      });
      return;
    }
    if (sel.isCollapsed) return; // čeká na výběr cílového textu
    el.focus();
    document.execCommand("styleWithCSS", false, true);
    document.execCommand("formatBlock", false, stetec.h3 ? "h3" : "div");
    for (const c of ["bold", "italic", "underline"]) {
      if (document.queryCommandState(c) !== stetec[c]) document.execCommand(c, false, null);
    }
    if (stetec.color) document.execCommand("foreColor", false, stetec.color);
    setStetec(null);
  };

  const TBtn = ({ onClick, children, title, active }) => (
    <button type="button" onClick={onClick} title={title}
      onMouseDown={(e) => e.preventDefault()} /* nebere fokus → výběr v editoru zůstane */
      className={`px-2 py-1 rounded-lg text-xs font-semibold border ${active
        ? "text-plum-700 bg-plum-50 border-plum-300"
        : "text-slate-600 hover:bg-white hover:border-slate-200 border-transparent"}`}>
      {children}
    </button>
  );

  // ── Editor ──
  if (openId && (isNew || current)) {
    return (
      <div className="space-y-3">
        <ConfirmDialog
          open={!!confirmState}
          title={confirmState?.title || ""}
          description={confirmState?.description}
          confirmLabel={confirmState?.confirmLabel || "Potvrdit"}
          variant="danger"
          onConfirm={() => { const fn = confirmState?.onConfirm; setConfirmState(null); if (fn) fn(); }}
          onCancel={() => setConfirmState(null)}
        />
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => { setOpenId(null); load(); }}
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800 border border-slate-200 rounded-lg px-2 py-1">
            <ChevronLeft className="w-3.5 h-3.5" /> Zpět na seznam
          </button>
          <input value={nazev} onChange={(e) => setNazev(e.target.value)} disabled={!jeKoncept}
            className="field flex-1 min-w-[200px] font-semibold" />
          {jeKoncept && canManage && (
            <button onClick={save} disabled={saving}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 border border-slate-300 rounded-lg px-2.5 py-1.5 disabled:opacity-50 min-h-[44px] sm:min-h-0">
              <Save className="w-3.5 h-3.5" /> {saved ? "Uloženo ✓" : "Uložit koncept"}
            </button>
          )}
        </div>

        {jeKoncept && canManage && konceptObnoven && (
          <div className="flex items-center gap-2 flex-wrap bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-900">
            <span className="font-bold">Neuložený koncept</span>
            <span className="w-px h-3 bg-current opacity-40" />
            <span>Text z {fmtDateTime(konceptObnoven.ulozeno)} se obnovil sám. Uložením se z něj stane uložený koncept reportu.</span>
            <span className="flex-1" />
            <button onClick={zahoditKoncept}
              className="text-[11px] font-semibold text-amber-800 hover:text-amber-900 border border-amber-300 rounded-lg px-2 py-1 min-h-[44px] sm:min-h-0">
              Zahodit
            </button>
            <button onClick={save} disabled={saving}
              className="btn btn-primary">
              Uložit koncept
            </button>
          </div>
        )}

        {typ === "konzultace" && (jeKoncept ? canManage : true) && (
          <div className="bg-sky-50 border border-sky-200 rounded-xl px-3 py-2.5 space-y-1.5">
            <div className="text-xs font-bold text-sky-800">📅 Datum konzultace</div>
            {jeKoncept && canManage ? (
              <>
                {volneTerminy.length > 0 && (
                  <div className="flex gap-1.5 flex-wrap">
                    {volneTerminy.slice(0, 5).map((t) => (
                      <button key={t.datum} type="button" onClick={() => setDatumKonz(t.datum)}
                        className={`text-[11px] font-medium rounded-full px-2.5 py-1 border ${datumKonz === t.datum
                          ? "bg-sky-600 text-white border-sky-600"
                          : "bg-white text-sky-700 border-sky-300 hover:border-sky-500"}`}>
                        {fmtDate(t.datum)}{t.cas ? ` ${t.cas}` : ""}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input type="date" value={datumKonz} onChange={(e) => setDatumKonz(e.target.value)}
                    className="field w-auto" />
                  <span className="text-[11px] text-sky-600">
                    {volneTerminy.length > 0 ? "termíny z Reservia bez reportu, nebo zadejte ručně" : "žádný Reservio termín bez reportu, zadejte ručně"}
                  </span>
                </div>
              </>
            ) : (
              <div className="text-xs text-sky-700">{datumKonz ? fmtDate(datumKonz) : "—"}</div>
            )}
          </div>
        )}

        {jeKoncept && canManage && typ === "konzultace" && (
          <div className="bg-brand-50 border border-brand-200 rounded-xl px-3 py-2.5">
            <button onClick={() => setPrepisOpen(!prepisOpen)} className="w-full text-left text-xs font-semibold text-brand-700">
              🎙️ Přepis konzultace → AI koncept {data?.maApiKlic === false ? "(chybí API klíč)" : ""} {prepisOpen ? "▴" : "▾"}
            </button>
            {prepisOpen && (
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-2">
                  <button onClick={() => prepisFileRef.current?.click()}
                    className="text-xs font-medium text-brand-700 bg-white border border-brand-300 rounded-lg px-2.5 py-1.5">
                    📄 Nahrát .txt soubor
                  </button>
                  <input ref={prepisFileRef} type="file" accept=".txt,.md,.vtt,.srt,text/plain" className="hidden"
                    onChange={(e) => nactiPrepisSoubor(e.target.files?.[0])} />
                  <span className="text-[11px] text-brand-500">nebo přepis vložte níže</span>
                </div>
                <textarea value={prepisText} onChange={(e) => setPrepisText(e.target.value)} rows={5}
                  placeholder="Sem vložte přepis hovoru s klientkou…"
                  className="field w-full font-mono" />
                <button onClick={aiZPrepisu} disabled={aiBusy}
                  className="btn btn-primary">
                  <Sparkles className="w-3.5 h-3.5" /> {aiBusy ? "AI píše koncept… (do minuty)" : "Vytvořit koncept z přepisu"}
                </button>
                <div className="text-[11px] text-brand-500">Koncept PŘEPÍŠE obsah editoru. Využije i dotazník a vypočtený příjem klientky, pokud existují.</div>
              </div>
            )}
          </div>
        )}

        {jeKoncept && canManage && typ === "analyza" && (
          <div className="bg-brand-50 border border-brand-200 rounded-xl px-3 py-2.5">
            <button onClick={() => setPrepisOpen(!prepisOpen)} className="w-full text-left text-xs font-semibold text-brand-700">
              📷 Fotky jídel → AI koncept analýzy {data?.maApiKlic === false ? "(chybí API klíč)" : ""} {prepisOpen ? "▴" : "▾"}
            </button>
            {prepisOpen && (
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => fotkyRef.current?.click()}
                    className="text-xs font-medium text-brand-700 bg-white border border-brand-300 rounded-lg px-2.5 py-1.5">
                    📷 Nahrát fotky jídel
                  </button>
                  <input ref={fotkyRef} type="file" accept="image/*" multiple className="hidden"
                    onChange={(e) => { pridejFotky(e.target.files); e.target.value = ""; }} />
                  <span className="text-[11px] text-brand-500">fotky jídel klientky z několika dnů (max 10)</span>
                </div>
                {fotky.length > 0 && (
                  <div className="flex gap-1.5 flex-wrap">
                    {fotky.map((f, i) => (
                      <div key={i} className="relative">
                        <img src={f} alt="" className="w-14 h-14 object-cover rounded-lg border border-brand-200" />
                        <button onClick={() => setFotky((p) => p.filter((_, x) => x !== i))} title="Odebrat"
                          className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-rose-500 text-white text-[11px] leading-4 text-center">✕</button>
                      </div>
                    ))}
                  </div>
                )}
                <textarea value={fotkyPopis} onChange={(e) => setFotkyPopis(e.target.value)} rows={3}
                  placeholder="Volitelný popis/komentář — které dny, časy jídel, co klientka říkala…"
                  className="field w-full" />
                <button onClick={aiZFotek} disabled={aiBusy || !fotky.length}
                  className="btn btn-primary">
                  <Sparkles className="w-3.5 h-3.5" /> {aiBusy ? "AI analyzuje fotky… (do minuty)" : `Vytvořit analýzu z ${fotky.length || ""} fotek`}
                </button>
                <div className="text-[11px] text-brand-500">Koncept PŘEPÍŠE obsah editoru. AI projde každou fotku a zhodnotí jídlo (✅/❌/🔜).</div>
              </div>
            )}
          </div>
        )}

        {jeKoncept ? (
          <div>
            <div className="flex gap-1 flex-wrap bg-slate-50 border border-slate-200 rounded-t-xl px-2 py-1.5">
              <TBtn onClick={() => cmd("undo")} title="Zpět (Ctrl+Z)">
                <span className="inline-flex items-center gap-1"><Undo2 className="w-4 h-4" strokeWidth={2.5} /> zpět</span>
              </TBtn>
              <TBtn onClick={() => cmd("redo")} title="Znovu (Ctrl+Shift+Z)">
                <Redo2 className="w-4 h-4" strokeWidth={2.5} />
              </TBtn>
              <span className="w-px bg-slate-200 mx-1" />
              <TBtn onClick={() => cmd("bold")} title="Tučně"><b>B</b></TBtn>
              <TBtn onClick={() => cmd("italic")} title="Kurzíva"><i>I</i></TBtn>
              <TBtn onClick={() => cmd("formatBlock", "h3")} title="Nadpis">H3</TBtn>
              <TBtn onClick={() => cmd("insertUnorderedList")} title="Seznam">• seznam</TBtn>
              <span className="w-px bg-slate-200 mx-1" />
              <TBtn onClick={() => vlozText("✅ ")} title="Co bylo dobře">✅</TBtn>
              <TBtn onClick={() => vlozText("❌ ")} title="Co bylo špatně">❌</TBtn>
              <TBtn onClick={() => vlozText("🔜 ")} title="Doporučení">🔜</TBtn>
              <span className="w-px bg-slate-200 mx-1" />
              <TBtn onClick={() => fileRef.current?.click()} title="Vložit obrázek"><ImageIcon className="w-3.5 h-3.5 inline" /> obrázek</TBtn>
              <TBtn onClick={otevriOdkaz} title="Odkaz">🔗</TBtn>
              <span className="w-px bg-slate-200 mx-1" />
              <TBtn onClick={sjednotFormat} title="Sjednotit písmo a velikost (vybraného textu, nebo celého když nic nevyberete)">🧹 sjednotit</TBtn>
              <TBtn onClick={stetecKlik} active={!!stetec}
                title={stetec ? "Formát zkopírován — vyberte text a klikněte znovu" : "Kopírovat formát: klikněte do zformátovaného textu a pak sem"}>
                🖌️ {stetec ? "označte text a klikněte" : "formát"}
              </TBtn>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { vlozObrazek(e.target.files?.[0]); e.target.value = ""; }} />
            </div>
            <Modal open={odkazOpen} onClose={() => setOdkazOpen(false)} title="Vložit odkaz" size="sm"
              footer={(
                <div className="flex justify-end gap-2">
                  <button type="button" className="btn btn-secondary" onClick={() => setOdkazOpen(false)}>Zrušit</button>
                  <button type="button" className="btn btn-primary" onClick={vlozOdkaz} disabled={!odkazUrl.trim()}>Vložit</button>
                </div>
              )}>
              <label className="label" htmlFor="nutri-odkaz-url">Adresa odkazu</label>
              <input id="nutri-odkaz-url" type="url" className="field" value={odkazUrl} autoFocus
                placeholder="https://…" onChange={(e) => setOdkazUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); vlozOdkaz(); } }} />
            </Modal>
            <div ref={editorRef} contentEditable suppressContentEditableWarning
              onInput={naplanujZapis}
              onBlur={zapisKoncept}
              onPaste={(e) => {
                const f = [...(e.clipboardData?.files || [])].find((x) => x.type.startsWith("image/"));
                if (f) { e.preventDefault(); vlozObrazek(f); return; }
                // Vložení textu odjinud: očistit cizí font/velikost/barvy,
                // strukturu (tučně, nadpisy, seznamy, odkazy) zachovat.
                const html = e.clipboardData?.getData("text/html");
                if (html) {
                  e.preventDefault();
                  editorRef.current?.focus();
                  document.execCommand("insertHTML", false, ocistiHtml(html));
                }
              }}
              className="border border-slate-200 border-t-0 rounded-b-xl px-4 py-3 min-h-[280px] text-sm leading-relaxed focus:outline-none prose-sm [&_h3]:font-bold [&_h3]:mt-3 [&_h3]:mb-1 [&_a]:text-plum-600 [&_a]:underline"
            />
          </div>
        ) : (
          <div className="border border-slate-200 rounded-xl px-4 py-3 text-sm leading-relaxed bg-slate-50 [&_h3]:font-bold [&_h3]:mt-3 [&_h3]:mb-1"
            dangerouslySetInnerHTML={{ __html: current?.obsah || "" }} />
        )}

        {jeKoncept && canManage && (
          <div className="bg-white border border-slate-200 rounded-xl px-3.5 py-3 space-y-2.5 shadow-sm">
            <div className="text-xs font-bold text-slate-700">✉️ Odeslání klientce</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input value={komu} onChange={(e) => setKomu(e.target.value)} placeholder="E-mail klientky"
                className="field w-auto" />
              <input value={predmet} onChange={(e) => setPredmet(e.target.value)} placeholder="Předmět"
                className="field w-auto" />
            </div>
            <div className="flex items-center gap-2 flex-wrap pt-0.5">
              <button onClick={odeslat} disabled={saving || !komu}
                className="btn btn-primary">
                <Send className="w-3.5 h-3.5" /> Odeslat klientce
              </button>
              <button onClick={() => { setPlanOpen(!planOpen); if (!planKdy) setPlanKdy(presetKdy(2, 8)); }} disabled={saving || !komu}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-1.5 disabled:opacity-50 border ${planOpen ? "text-sky-800 bg-sky-100 border-sky-300" : "text-sky-700 bg-sky-50 hover:bg-sky-100 border-sky-200"}`}>
                ⏰ Poslat později {planOpen ? "▴" : "▾"}
              </button>
              <button onClick={ulozitAZavrit} disabled={saving}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 border border-slate-300 rounded-lg px-3 py-1.5 disabled:opacity-50 min-h-[44px] sm:min-h-0">
                <Save className="w-3.5 h-3.5" /> Jen uložit
              </button>
              <span className="flex-1" />
              {isNew ? (
                <button onClick={zahoditRozepsany}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-600 hover:text-rose-700 border border-rose-200 hover:border-rose-300 bg-rose-50 rounded-lg px-3 py-1.5 min-h-[44px] sm:min-h-0">
                  <Trash2 className="w-3.5 h-3.5" /> Zahodit
                </button>
              ) : (
                <button onClick={() => smazat(current)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-rose-500 hover:text-rose-700 px-2 py-1.5">
                  <Trash2 className="w-3.5 h-3.5" /> Smazat
                </button>
              )}
            </div>
            {planOpen && (
              <div className="bg-sky-50 border border-sky-200 rounded-xl px-3 py-2.5 space-y-2">
                <div className="text-[11px] font-bold text-sky-800 uppercase tracking-wide">⏰ Naplánovat odeslání</div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button onClick={() => setPlanKdy(presetKdy(1, 8))}
                    className={`text-[11px] font-medium rounded-full px-2.5 py-1 border ${planKdy === presetKdy(1, 8) ? "bg-sky-600 text-white border-sky-600" : "bg-white text-sky-700 border-sky-300 hover:bg-sky-100"}`}>
                    Zítra 8:00
                  </button>
                  <button onClick={() => setPlanKdy(presetKdy(2, 8))}
                    className={`text-[11px] font-medium rounded-full px-2.5 py-1 border ${planKdy === presetKdy(2, 8) ? "bg-sky-600 text-white border-sky-600" : "bg-white text-sky-700 border-sky-300 hover:bg-sky-100"}`}>
                    Za 2 dny 8:00
                  </button>
                  <button onClick={() => setPlanKdy(presetKdy(7, 8))}
                    className={`text-[11px] font-medium rounded-full px-2.5 py-1 border ${planKdy === presetKdy(7, 8) ? "bg-sky-600 text-white border-sky-600" : "bg-white text-sky-700 border-sky-300 hover:bg-sky-100"}`}>
                    Za týden 8:00
                  </button>
                  <span className="text-[11px] text-sky-600">nebo vlastní:</span>
                  <input type="datetime-local" value={planKdy} onChange={(e) => setPlanKdy(e.target.value)}
                    className="field w-auto" />
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={naplanovat} disabled={saving || !planKdy || !komu}
                    className="btn btn-primary">
                    ⏰ Naplánovat na {planKdy ? fmtDateTime(planKdy) : "…"}
                  </button>
                  <span className="text-[11px] text-sky-600">Report se uloží, zamkne a odešle se sám (přesnost do 10 minut). Plán jde kdykoli zrušit.</span>
                </div>
              </div>
            )}
          </div>
        )}
        {current?.stav === "naplanovano" && (
          <div className="flex items-center gap-2 flex-wrap bg-sky-50 border border-sky-200 rounded-xl px-3 py-2.5">
            <span className="text-xs font-semibold text-sky-800">⏰ Naplánováno na {fmtDateTime(current.naplanovano_at)} → {current.naplanovano_komu}</span>
            <span className="flex-1" />
            {canManage && (
              <button onClick={() => zrusitPlan(current)}
                className="text-xs font-medium text-sky-700 hover:text-sky-900 border border-sky-300 bg-white rounded-lg px-2.5 py-1">
                Zrušit plán (zpět na koncept)
              </button>
            )}
          </div>
        )}
        {!jeKoncept && current && current.stav !== "naplanovano" && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-500">✉️ Odesláno {fmtDate(current.odeslano_at)} na {current.odeslano_komu}, {current.autor}</span>
            <span className="flex-1" />
            {isOwner && (
              <button onClick={() => smazat(current)}
                className="inline-flex items-center gap-1 text-xs font-medium text-rose-500 hover:text-rose-700 px-2 py-1">
                <Trash2 className="w-3.5 h-3.5" /> Smazat
              </button>
            )}
          </div>
        )}
        {err && <div className="text-xs text-rose-600">{err}</div>}
      </div>
    );
  }

  // ── Seznam ──
  // Rozepsaný nový report ještě není v datech ze serveru. Kdyby se na něj
  // nijak neupozornilo, nikdo by o něm nevěděl a text by tiše zestárl.
  const rozepsane = canManage
    ? ["konzultace", "analyza"]
        .map((t) => { const o = nactiKoncept(`new-${t}`); return o ? { typ: t, ulozeno: o.ulozeno } : null; })
        .filter(Boolean)
    : [];

  return (
    <div className="space-y-2">
        <ConfirmDialog
          open={!!confirmState}
          title={confirmState?.title || ""}
          description={confirmState?.description}
          confirmLabel={confirmState?.confirmLabel || "Potvrdit"}
          variant="danger"
          onConfirm={() => { const fn = confirmState?.onConfirm; setConfirmState(null); if (fn) fn(); }}
          onCancel={() => setConfirmState(null)}
        />
      {canManage && (
        <div className="flex gap-2.5 flex-wrap">
          <button onClick={() => openNew("konzultace")}
            className="btn btn-primary">
            <FileText className="w-4 h-4" /> + Report z konzultace
          </button>
          <button onClick={() => openNew("analyza")}
            className="btn btn-primary">
            <Camera className="w-4 h-4" /> + Analýza jídelníčku
          </button>
        </div>
      )}
      {rozepsane.map((r) => (
        <div key={r.typ} className="flex items-center gap-2 flex-wrap bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-900">
          <span className="font-bold">Rozepsaný neuložený text</span>
          <span className="w-px h-3 bg-current opacity-40" />
          <span>{r.typ === "analyza" ? "Analýza jídelníčku" : "Report z konzultace"} z {fmtDateTime(r.ulozeno)} se neuložil. Otevřením se text vrátí.</span>
          <span className="flex-1" />
          <button onClick={() => openNew(r.typ)}
            className="btn btn-primary">
            Otevřít
          </button>
        </div>
      ))}
      {reporty.length === 0 ? (
        <div className="text-sm text-slate-400 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5">
          Zatím žádný report. {canManage ? "Vytvořte první tlačítkem výše — nebo nahrajte přepis konzultace a nechte AI připravit koncept." : ""}
        </div>
      ) : (
        reporty.map((rep) => (
          <div key={rep.id} onClick={() => openEditor(rep)}
            className="flex cursor-pointer flex-wrap items-center gap-x-2.5 gap-y-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 hover:border-emerald-300">
            <span className={`w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0 ${rep.typ === "analyza" ? "bg-brand-100" : "bg-emerald-100"}`}>
              {rep.typ === "analyza" ? <Camera className="w-3.5 h-3.5 text-brand-600" /> : <FileText className="w-3.5 h-3.5 text-emerald-600" />}
            </span>
            <div className="min-w-0 flex-1 basis-[60%] sm:basis-auto">
              <div className="text-sm font-medium text-slate-800 truncate">{rep.nazev}</div>
              <div className="truncate text-[11px] text-slate-400">
                {rep.autor}, {fmtDate(rep.updated_at)}{rep.datum_konzultace ? `, konzultace ${fmtDate(rep.datum_konzultace)}` : ""}
              </div>
            </div>
            {rep.stav === "odeslano" ? (
              <span className="order-last inline-block max-w-full basis-full truncate rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 sm:order-none sm:basis-auto sm:max-w-[50%]">
                ✉️ {fmtDate(rep.odeslano_at)}, {rep.odeslano_komu}
              </span>
            ) : rep.stav === "naplanovano" ? (
              <span className="order-last inline-block max-w-full basis-full truncate rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700 sm:order-none sm:basis-auto sm:max-w-[50%]">
                ⏰ {fmtDateTime(rep.naplanovano_at)}
              </span>
            ) : (
              <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">koncept</span>
            )}
            {(rep.stav === "koncept" ? canManage : isOwner) && (
              <button onClick={(e) => { e.stopPropagation(); smazat(rep); }} title="Smazat"
                className="text-slate-300 hover:text-rose-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
            )}
          </div>
        ))
      )}
      {/* Historie nutričních konzultací (6.8.2026) — zdroj: Reservio rezervace (PG bookings). */}
      <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
        <button type="button" onClick={() => setHistOpen((v) => !v)}
          className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50">
          {histOpen ? <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />}
          <CalendarDays className="w-4 h-4 text-emerald-600 flex-shrink-0" />
          <span className="text-sm font-semibold text-slate-700">Historie nutričních konzultací</span>
          <span className="text-[11px] font-medium text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 ml-auto">{historie.length}</span>
        </button>
        {histOpen && (
          <div className="border-t border-slate-100 px-3 py-2 space-y-1.5">
            {historie.length === 0 ? (
              <div className="text-xs text-slate-400">Zatím žádná nutriční konzultace v rezervacích.</div>
            ) : (
              historie.map((t, i) => (
                <div key={`${t.datum}-${i}`} className="flex items-center gap-2 text-[12px] text-slate-600">
                  <span className="font-medium text-slate-800 whitespace-nowrap">{fmtDate(t.datum)}</span>
                  {t.cas && (<>
                    <span aria-hidden className="w-px h-3 bg-current opacity-40" />
                    <span className="whitespace-nowrap">{t.cas}</span>
                  </>)}
                  <span aria-hidden className="w-px h-3 bg-current opacity-40" />
                  <span className="truncate">{t.trener || "—"}</span>
                  {t.maReport && (<>
                    <span aria-hidden className="w-px h-3 bg-current opacity-40" />
                    <span className="text-emerald-700 whitespace-nowrap">report</span>
                  </>)}
                </div>
              ))
            )}
          </div>
        )}
      </div>
      {err && <div className="text-xs text-rose-600">{err}</div>}
    </div>
  );
}
